import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import { useSidebar } from "../../contexts/SidebarContext";
import { useArtist } from "../../contexts/ArtistContext";
import ArtistSwitchingLoader from "../UI/ArtistSwitchingLoader";

const MainLayout: React.FC<{ showSidebar?: boolean; children?: React.ReactNode }> = ({
  showSidebar = true,
  children,
}) => {
  const { sidebarOpen, setSidebarOpen, toggleSidebar } = useSidebar();
  const { switchingArtist } = useArtist();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const [viewportSize, setViewportSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(true); // Open by default on desktop
      }
    };

    handleResize(); // Initial run
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const readViewport = () => {
      const width = window.visualViewport?.width || window.innerWidth;
      const height = window.visualViewport?.height || window.innerHeight;
      const roundedWidth = Math.round(width);
      const roundedHeight = Math.round(height);
      setViewportSize({
        width: roundedWidth,
        height: roundedHeight,
      });
      document.documentElement.style.setProperty("--nova-viewport-width", `${roundedWidth}px`);
      document.documentElement.style.setProperty("--nova-viewport-height", `${roundedHeight}px`);
    };

    readViewport();
    window.addEventListener("resize", readViewport);
    window.visualViewport?.addEventListener("resize", readViewport);
    window.visualViewport?.addEventListener("scroll", readViewport);

    return () => {
      window.removeEventListener("resize", readViewport);
      window.visualViewport?.removeEventListener("resize", readViewport);
      window.visualViewport?.removeEventListener("scroll", readViewport);
    };
  }, []);

  useEffect(() => {
    const updateNavbarHeight = () => {
      const headerEl = document.querySelector("header.navbar-menu-item") as HTMLElement | null;
      const height = headerEl?.offsetHeight || 0;
      setNavbarHeight(height);
      document.documentElement.style.setProperty("--nova-navbar-height", `${height}px`);
    };

    updateNavbarHeight();
    window.addEventListener("resize", updateNavbarHeight);

    return () => {
      window.removeEventListener("resize", updateNavbarHeight);
    };
  }, []);

  useEffect(() => {
    // Lock page-level scroll for authenticated layout.
    document.documentElement.classList.add("app-shell-scroll-lock");
    document.body.classList.add("app-shell-scroll-lock");
    return () => {
      document.documentElement.classList.remove("app-shell-scroll-lock");
      document.body.classList.remove("app-shell-scroll-lock");
    };
  }, []);

  const shellHeightPx = Math.max(0, viewportSize.height - navbarHeight);

  return (
    <div
      className="flex flex-col overflow-hidden"
      style={{
        width: "100%",
        maxWidth: `${viewportSize.width}px`,
        height: `${shellHeightPx}px`,
        minHeight: `${shellHeightPx}px`,
        maxHeight: `${shellHeightPx}px`,
      }}
    >
      {switchingArtist && <ArtistSwitchingLoader />}
      <div className="flex flex-1 overflow-hidden">
        {showSidebar && (
          <Sidebar
            isOpen={sidebarOpen}
            onClose={toggleSidebar}
            onToggle={toggleSidebar}
            isDesktop={isDesktop}
          />
        )}
        <main className="flex-1 min-h-0 bg-nova-dashboard-900 bg-[#F1F0EC] overflow-y-auto overscroll-y-contain">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
